import type { KeyboardEvent } from 'react';
import { IntervalConnector } from './IntervalConnector';
import { MonthBands } from './MonthBands';
import { ScanMarker } from './ScanMarker';
import { TimelineAxis } from './TimelineAxis';
import { TRACK_HEIGHT } from './geometry';
import type { MonthBand, TimelineInterval, TimelineScan } from './scanScale';
import type { ContainerRef } from './useContainerWidth';

interface TimelineTrackProps {
    trackRef: ContainerRef<HTMLDivElement>;
    width: number;
    bands: MonthBand[];
    intervals: TimelineInterval[];
    points: TimelineScan[];
    selectedX: number | null;
    /** Omitted when the selected scan is scrolled out of view and has no marker to point at. */
    activeDescendantId?: string;
    idPrefix: string;
    onSelect: (scanId: number) => void;
    /** Attaches the non-passive wheel listener; composed with trackRef on the node. */
    wheelRef: ContainerRef<HTMLDivElement>;
    onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
    /** Passed to each marker; false numbers the scans rather than the aligners. */
    tracksAligners?: boolean;
}

function TimelineTrack({
    trackRef,
    width,
    bands,
    intervals,
    points,
    selectedX,
    activeDescendantId,
    idPrefix,
    onSelect,
    wheelRef,
    onKeyDown,
    tracksAligners = true
}: TimelineTrackProps) {
    return (
        <div
            ref={(node) => {
                trackRef(node);
                wheelRef(node);
            }}
            role="listbox"
            aria-label="Scan timeline. Scroll to zoom around the cursor."
            aria-orientation="horizontal"
            aria-activedescendant={activeDescendantId}
            tabIndex={0}
            onKeyDown={onKeyDown}
            className="relative w-full overflow-hidden focus:outline-none focus:ring-2 focus:ring-inset focus:ring-gray-300"
            style={{ height: TRACK_HEIGHT }}
        >
            <MonthBands bands={bands} height={TRACK_HEIGHT} />
            <TimelineAxis width={width} selectedX={selectedX} />
            {intervals.map((interval) => (
                <IntervalConnector key={interval.key} interval={interval} />
            ))}
            {points.map((point) => (
                <ScanMarker
                    key={point.scan.scanId}
                    id={`${idPrefix}-${point.scan.scanId}`}
                    point={point}
                    onSelect={onSelect}
                    tracksAligners={tracksAligners}
                />
            ))}
        </div>
    );
}

export { TimelineTrack };
